import React, { useState, useEffect, useRef } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Code2, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "../lib/api";

export default function EmbedBadgeSection({ jobId }) {
  const [theme, setTheme] = useState("light");
  const [size, setSize] = useState("md");
  const [copied, setCopied] = useState(false);
  const previewRef = useRef(null);

  const snippet = `<script async src="${BACKEND_URL}/api/embed/badge.js" data-job="${jobId}" data-theme="${theme}" data-size="${size}"></script>`;

  // Render a live preview by injecting the same JS into a sandboxed div.
  // Uses replaceChildren() (no innerHTML) — avoids any HTML-parsing surface.
  useEffect(() => {
    const container = previewRef.current;
    if (!container) return;
    container.replaceChildren();
    const tag = document.createElement("script");
    tag.src = `${BACKEND_URL}/api/embed/badge.js`;
    tag.setAttribute("data-job", jobId);
    tag.setAttribute("data-theme", theme);
    tag.setAttribute("data-size", size);
    tag.async = true;
    container.appendChild(tag);
    return () => container.replaceChildren();
  }, [jobId, theme, size]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success("Embed snippet copied — paste it into your careers page.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  return (
    <Card className="p-6 mt-6">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Code2 className="h-5 w-5 text-[#002FA7]" />
          <h2 className="font-display text-xl font-bold tracking-tight">Embed this job on your site</h2>
          <Badge className="tag-mono bg-blue-50 text-blue-700 border border-blue-200">free</Badge>
        </div>
        <a
          href="https://qatestology.com/jobs"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 tag-mono"
        >
          docs <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <p className="text-sm text-zinc-600 mb-4">
        Drop this snippet onto your company's careers page. Candidates click → land on this job's apply
        page on QaTestology. Every click is tracked so you can see ROI in your dashboard.
      </p>

      {/* Controls */}
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="text-xs">Theme</Label>
          <div className="mt-1.5 inline-flex p-1 bg-zinc-100 rounded-md">
            {["light", "dark"].map(t => (
              <button
                key={t}
                onClick={()=>setTheme(t)}
                data-testid={`embed-theme-${t}`}
                className={`px-3 py-1.5 rounded text-xs font-medium capitalize transition-colors ${
                  theme === t ? "bg-white shadow-sm text-zinc-950" : "text-zinc-600 hover:text-zinc-950"
                }`}
              >{t}</button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs">Size</Label>
          <div className="mt-1.5 inline-flex p-1 bg-zinc-100 rounded-md">
            {["sm", "md", "lg"].map(s => (
              <button
                key={s}
                onClick={()=>setSize(s)}
                data-testid={`embed-size-${s}`}
                className={`px-3 py-1.5 rounded text-xs font-medium uppercase tag-mono transition-colors ${
                  size === s ? "bg-white shadow-sm text-zinc-950" : "text-zinc-600 hover:text-zinc-950"
                }`}
              >{s}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="mb-4">
        <Label className="text-xs">Live preview</Label>
        <div className={`mt-1.5 p-6 rounded-md border ${theme === "dark" ? "bg-zinc-100 border-zinc-200" : "bg-white border-zinc-200"} flex items-center justify-center min-h-[80px]`}>
          <div ref={previewRef} data-testid="embed-preview" />
        </div>
      </div>

      {/* Snippet */}
      <div>
        <Label className="text-xs">Copy & paste this snippet</Label>
        <div className="mt-1.5 relative">
          <pre data-testid="embed-snippet" className="font-mono text-xs bg-zinc-950 text-emerald-300 rounded-md p-4 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed">{snippet}</pre>
          <Button
            size="sm"
            onClick={copy}
            data-testid="embed-copy-btn"
            className="absolute top-2 right-2 bg-zinc-800 hover:bg-zinc-700 text-white"
          >
            {copied ? <><Check className="h-3.5 w-3.5 mr-1 text-emerald-400" /> Copied</> : <><Copy className="h-3.5 w-3.5 mr-1" /> Copy</>}
          </Button>
        </div>
        <p className="mt-2 text-xs text-zinc-500">
          Tip: paste it inside any HTML element on your careers page. The badge inherits surrounding fonts and lays out inline.
        </p>
      </div>
    </Card>
  );
}
